import { PERMISSION_NO_GRANT_TOOLTIP } from './utils';

/**
 * NEX-405: target-role eligibility for the "Nexa Context Center" KV permission.
 * The checkbox renders generically from /users/meta; this is the one handwritten
 * rule layered on the server's per-operator canGrant gate.
 */
export const CONTEXT_CENTER_PERMISSION_KEY = 'canAccessContextCenter';

export const CONTEXT_CENTER_ROLE_INELIGIBLE_TOOLTIP =
  'Only Admin and Staff users can use Context Center';

/**
 * Pulse role names in the Admin and Staff tiers; only these can use Context Center.
 *
 * KNOWN COUPLING: duplicates Instant-Filter-Server `deriveNexaTier`
 * (src/nexa/nexaRole.ts; strings from src/common/enums/userRoles.ts). A role added
 * to either tier in IFS must be added here too. "Read Only" is its own tier there
 * and, like every other role, is ineligible. IFS enforces the same rule server-side.
 */
export const CONTEXT_CENTER_ELIGIBLE_ROLE_NAMES: ReadonlySet<string> = new Set<string>([
  // Admin tier
  'Admin',
  'Conviva Admin',
  'Super Admin',
  'Root Admin',
  'CDN Admin',
  'Infrastructure',
  // Staff tier
  'Staff',
  'Backstage Only',
  'CDN Staff',
  'Developer',
  'Embedded Pulse',
  'Provisioning',
]);

export const isContextCenterEligibleRole = (roleName?: string): boolean =>
  !!roleName && CONTEXT_CENTER_ELIGIBLE_ROLE_NAMES.has(roleName);

/**
 * Drop the Context Center permission unless `nexa-context-center` is enabled for the
 * current user. The features API already resolves internal-only vs everyone; a falsy
 * value means this operator must not see or edit the permission.
 */
export const filterPermissionForDisabledFlag = <T extends { key: string }>(
  defs: readonly T[],
  flagValue: unknown,
): T[] => {
  if (flagValue) {
    return [...defs];
  }
  return defs.filter((def) => def.key !== CONTEXT_CENTER_PERMISSION_KEY);
};

/** Resolve the form's watched roleId through mergedRoleList, so unsaved role changes count. */
export const getRoleNameById = (
  roleList: ReadonlyArray<{ roleId?: number | null; roleName?: string | null }>,
  roleId?: number,
): string | undefined => {
  if (roleId === undefined) {
    return undefined;
  }
  return roleList.find((role) => role.roleId === roleId)?.roleName ?? undefined;
};

export interface KvPermissionCheckboxState {
  disabled: boolean;
  tooltip?: string;
}

/**
 * Disabled state and tooltip for a generically rendered KV boolean checkbox.
 * The operator-grant rule wins; then, for Context Center only, the target-role rule.
 */
export const getKvPermissionCheckboxState = (
  def: { key: string; description?: string },
  grantable: boolean,
  targetRoleName?: string,
): KvPermissionCheckboxState => {
  if (!grantable) {
    return { disabled: true, tooltip: PERMISSION_NO_GRANT_TOOLTIP };
  }
  if (def.key === CONTEXT_CENTER_PERMISSION_KEY && !isContextCenterEligibleRole(targetRoleName)) {
    return { disabled: true, tooltip: CONTEXT_CENTER_ROLE_INELIGIBLE_TOOLTIP };
  }
  return { disabled: false, tooltip: def.description || undefined };
};
